import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  StorageService,
  type NoticeKey,
  SUPPRESSIBLE_NOTICE_KEYS,
} from '@/services/storage';

interface SuppressibleNoticesContextType {
  isSuppressed: (k: NoticeKey) => boolean;
  suppress: (k: NoticeKey) => void;
  unsuppress: (k: NoticeKey) => void;
  clearAll: () => Promise<void>;
  loaded: boolean;
}

const SuppressibleNoticesContext = createContext<
  SuppressibleNoticesContextType | undefined
>(undefined);

interface SuppressibleNoticesProviderProps {
  children: React.ReactNode;
}

export const SuppressibleNoticesProvider: React.FC<
  SuppressibleNoticesProviderProps
> = ({ children }) => {
  const [suppressedState, setSuppressedState] = useState<
    Record<NoticeKey, boolean>
  >(
    () =>
      Object.fromEntries(
        Object.keys(SUPPRESSIBLE_NOTICE_KEYS).map(k => [k, false])
      ) as Record<NoticeKey, boolean>
  );
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all(
      (Object.keys(SUPPRESSIBLE_NOTICE_KEYS) as NoticeKey[]).map(k =>
        StorageService.getSuppressedNotice(k).then(v => [k, v] as const)
      )
    ).then(entries => {
      setSuppressedState(
        Object.fromEntries(entries) as Record<NoticeKey, boolean>
      );
      setLoaded(true);
    });
  }, []);

  const isSuppressed = useCallback(
    (k: NoticeKey): boolean => suppressedState[k],
    [suppressedState]
  );

  const suppress = useCallback((k: NoticeKey): void => {
    setSuppressedState(s => ({ ...s, [k]: true }));
    void StorageService.setNoticeSuppressed(k);
  }, []);

  const unsuppress = useCallback((k: NoticeKey): void => {
    setSuppressedState(s => ({ ...s, [k]: false }));
    void StorageService.clearSuppressedNotice(k);
  }, []);

  const clearAll = useCallback(async (): Promise<void> => {
    setSuppressedState(
      Object.fromEntries(
        Object.keys(SUPPRESSIBLE_NOTICE_KEYS).map(k => [k, false])
      ) as Record<NoticeKey, boolean>
    );
    await StorageService.clearAllSuppressedNotices();
  }, []);

  const value = useMemo(
    () => ({ isSuppressed, suppress, unsuppress, clearAll, loaded }),
    [isSuppressed, suppress, unsuppress, clearAll, loaded]
  );

  return (
    <SuppressibleNoticesContext.Provider value={value}>
      {children}
    </SuppressibleNoticesContext.Provider>
  );
};

export function useSuppressibleNotices(): SuppressibleNoticesContextType {
  const ctx = useContext(SuppressibleNoticesContext);
  if (ctx === undefined) {
    throw new Error(
      'useSuppressibleNotices must be used within a SuppressibleNoticesProvider'
    );
  }
  return ctx;
}
